/**
 * Centralized environment variable access for the Vite SPA.
 *
 * In Vite, client-exposed env vars must be prefixed with VITE_ and
 * accessed via import.meta.env.VITE_*.
 *
 * Values are read lazily via getters so the module can be imported safely.
 */

function get(key: string): string | undefined {
  return (import.meta.env as Record<string, string | undefined>)[key];
}

function getOrDefault(key: string, fallback: string): string {
  return get(key) ?? fallback;
}

export const env = {
  // Core
  get nodeEnv() {
    return getOrDefault("MODE", "development");
  },
  get isProduction() {
    return import.meta.env.PROD;
  },
  get appUrl() {
    return get("VITE_APP_URL");
  },
  /** Base URL for API calls (e.g. "https://api.wiserstudio.ai"). No trailing slash. */
  get apiUrl() {
    return get("VITE_API_URL") ?? "";
  },
  /** Base URL for Core API calls. Falls back to apiUrl, then empty (same origin). */
  get coreUrl() {
    return get("VITE_CORE_URL") ?? get("VITE_API_URL") ?? "";
  },

  // Google OAuth
  get googleClientId() {
    return get("VITE_GOOGLE_CLIENT_ID");
  },

  // Analytics
  get gtmId() {
    return get("VITE_GTM_ID");
  },
  get rudderstackWriteKey() {
    return get("VITE_RUDDERSTACK_WRITE_KEY");
  },
  get rudderstackDataPlaneUrl() {
    return get("VITE_RUDDERSTACK_DATA_PLANE_URL");
  },
  get mixpanelToken() {
    return get("VITE_MIXPANEL_TOKEN");
  },

  // Avatar
  get avatarProvider() {
    return get("VITE_AVATAR_PROVIDER");
  },
  get simliApiKey() {
    return get("VITE_SIMLI_API_KEY");
  },

  // Companion / Pipecat
  get companionRuntime() {
    return get("VITE_COMPANION_RUNTIME");
  },
  get scoutManagedAgentResearch() {
    // Defaults ON — the Research tab "Start Research" button dispatches Agent
    // Scout via /core/agent/managed-chat. Set VITE_SCOUT_MANAGED_AGENT=false
    // to fall back to the legacy /core/coagent/ads-research SSE pipeline.
    return get("VITE_SCOUT_MANAGED_AGENT") !== "false";
  },
  get pipecatWsUrl() {
    return get("VITE_PIPECAT_WS_URL");
  },

  // Stripe
  get stripePublishableKey() {
    return get("VITE_STRIPE_KEY");
  },

  // Experiments
  /** Mirrors core's EXPERIMENT_TARGETED_RESEARCH. When on, the Studio shows
   *  a "Targeted research (experimental)" pill next to the Run button so
   *  the user knows the new pipeline is engaged. Core does the actual
   *  routing — the FE flag is purely cosmetic. */
  get experimentTargetedResearch() {
    return getOrDefault("VITE_EXPERIMENT_TARGETED_RESEARCH", "0") === "1";
  },
  /** Gates the legacy "Ads Studio" page (`/ads`) and its sidebar entry.
   *  Hidden by default — set VITE_ENABLE_ADS_STUDIO=true to expose it. */
  get adsStudioEnabled() {
    const raw = get("VITE_ENABLE_ADS_STUDIO");
    return raw === "1" || raw === "true";
  },
};
